import CardPayment from './CardPayment';
import CashPayment from './CashPayment';
import { CardTransaction, CashTransaction, Invoice, PaymentDepartment, PaymentSessionEmailEntry } from './types';

class PaymentSession {
  cardPayments: CardPayment[];
  cashPayments: CashPayment[];
  CardTransactions: CardTransaction[];
  CashTransactions: CashTransaction[];
  amount: number;
  branchId: number;
  branchName: string;
  customerName: string;
  customerNum: string;
  customerType: string;
  department: PaymentDepartment;
  id: string;
  invoices: Invoice[];
  status: string;
  userInitials: string;
  username: string;
  emails?: PaymentSessionEmailEntry[];

  constructor(session: PaymentSession) {
    Object.assign(this, session);
    this.cardPayments = (this.CardTransactions || []).map(tx => new CardPayment(tx));
    this.cashPayments = (this.CashTransactions || []).map(tx => new CashPayment(tx));
  }

  isClosed() {
    return this.status !== 'open';
  }

  getTotalPaid() {
    let amount = 0;
    for (const payment of this.cardPayments) {
      if (payment.transactionType !== 'sale') {
        continue;
      }

      if (payment.isApproved() && !payment.isVoid()) {
        amount += payment.amount;
      }

      if (payment.hasRefunds()) {
        for (const refund of payment.refunds || []) {
          amount -= refund.amount;
        }
      }
    }

    amount += this.getCashPaid();

    return parseFloat(amount.toFixed(2));
  }

  getTotalRefunded() {
    let total = 0;
    for (const payment of this.cardPayments) {
      if (
        payment.sessionId === this.id &&
        ['void', 'refund'].includes(payment.transactionType) &&
        payment.isApproved()
      ) {
        total += Math.abs(payment.amount);
      }
    }

    for (const payment of this.cashPayments) {
      if (payment.sessionId === this.id && payment.transactionType === 'refund') {
        total += Math.abs(payment.amount);
      }
    }

    return parseFloat(total.toFixed(2));
  }

  /**
   * Get refund amounts by invoice num.
   * Not all refunds are associated with an invoice directly.
   * Single invoice refunds can be implied session.invoices
   */
  getRefundsByInvoice() {
    let refunds = {} as { [x: string]: number };
    for (const payment of this.cardPayments) {
      if (payment.transactionType === 'sale' && payment.isApproved() && payment.refunds && payment.refunds.length) {
        for (const refund of payment.refunds) {
          // not all refunds will have invoiceNum
          if (refund.invoiceNum) {
            if (!refunds[refund.invoiceNum]) {
              refunds[refund.invoiceNum] = 0;
            }
            refunds[refund.invoiceNum] += refund.amount;
          }
        }
      }
    }

    for (const payment of this.cashPayments) {
      if (
        payment.sessionId === this.id &&
        payment.transactionType === 'sale' &&
        payment.refunds &&
        payment.refunds.length
      ) {
        for (const refund of payment.refunds) {
          if (refund.invoiceNum) {
            if (!refunds[refund.invoiceNum]) {
              refunds[refund.invoiceNum] = 0;
            }
            refunds[refund.invoiceNum] += refund.amount;
          }
        }
      }
    }

    return refunds;
  }

  getCashPaid() {
    let cashAmount = 0;
    for (const cashPayment of this.cashPayments) {
      cashAmount += cashPayment.amount;
    }

    return parseFloat(cashAmount.toFixed(2));
  }

  getOutstandingAmount() {
    return parseFloat((this.amount - this.getTotalPaid()).toFixed(2));
  }

  canRefund() {
    return !!this.getTotalPaid();
  }
}

export default PaymentSession;
